<template>
  <div class="credit-usage-summary-card relative overflow-hidden">
    <UsageSummaryCard
      title="Credit Usage Summary"
      label="Credits used"
      :used="error ? null : creditsUsed"
      :limit="error ? null : creditLimit"
      :percent-text="limitUsedPercent"
      :width="limitUsedWidth"
      :color="meterColor"
      :is-loading="isLoading"
      :is-limit-masked="isCreditLimitMasked"
    >
      <template v-if="showPlatformUpsell" #title-action>
        <BillingCTA
          :button-text="
            isOrderFormLegacy ? 'Talk to Sales' : 'Unlock platform features'
          "
          size="x-small"
          :billing-interaction="platformUpsellInteraction"
          class="font-normal relative z-40"
          data-testid="credit-usage-summary-platform-cta"
        />
      </template>
      <template #sub-line>
        <span v-if="isNotPaying" data-testid="credit-usage-summary-free-plan">
          Credits available on your free plan
        </span>
        <span
          v-else-if="creditResetDate"
          data-testid="credit-usage-summary-reset"
        >
          {{ creditResetLabel
          }}<span v-if="!isCreditLimitMasked" class="font-bold">{{
            creditResetDate
          }}</span>
        </span>
        <span v-else />
      </template>
      <template #action>
        <BittsButton
          variant="ghost"
          size="x-small"
          text="See usage"
          :disabled="!!error"
          data-testid="credit-usage-summary-see-usage"
          @click="emit('see-usage')"
        />
      </template>
      <template #callout>
        <BittsCallout
          v-if="error"
          type="danger"
          size="x-small"
          title="We couldn't load your credit usage"
          subtitle="Please try again in a few moments."
          class="mt-16"
          data-testid="credit-usage-summary-error"
          action-text="Retry"
          @bitts-callout-action="emit('retry')"
        />
        <BittsCallout
          v-if="!error && isCreditLimitMasked"
          type="info"
          size="x-small"
          title="During beta, credit usage is uncapped."
          subtitle="These numbers are for informational purposes only. Starting September 1st, credit limits based on your plan will be enforced."
          class="mt-16"
          data-testid="credit-usage-summary-beta-notice"
        />
        <UsageLimitCallout
          v-if="!error && creditLimitCalloutContent"
          class="mt-16"
          :content="creditLimitCalloutContent"
          @contact-us="onCreditLimitContactUs"
        />
      </template>
      <template #footer>
        <div>
          <p class="text-neutral-text-weak mb-4">
            {{ footerMessage }}
          </p>
          <LearnMoreLink
            text="Learn how credits work"
            url="https://help.crossbeam.com/en/articles/15588582-crossbeam-credits-faq"
            event-site="credit_usage_summary_card"
          />
        </div>
        <BittsButton
          v-if="isNotPaying"
          type="upsell"
          size="x-small"
          text="Upgrade"
          :left-icon="['fak', 'upgrade']"
          data-testid="credit-usage-summary-upgrade"
          @click="onUpgradeClicked"
        />
        <BittsButton
          v-else
          type="primary"
          variant="outline"
          size="x-small"
          text="Contact us"
          data-testid="credit-usage-summary-contact-us"
          @click="onContactUsClicked"
        />
      </template>
    </UsageSummaryCard>
    <UpsellOverlay
      v-if="showPlatformUpsell"
      title="Unlock credits"
      text="Credits power AI partner recommendations, data partner AI in agents, and advanced collaboration tools. Unlock platform access to turn them on."
      :billing-interaction="platformUpsellInteraction"
      :show-button="false"
      class="inset-0 rounded-b-16"
      data-testid="credit-usage-summary-upsell-overlay"
    />
  </div>
</template>

<script setup lang="ts">
import { BittsButton, BittsCallout } from '@crossbeam/bitts';
import { EVENT_SITES, PLAN_AND_BILLING_CTAS } from '@crossbeam/itly';

import { storeToRefs } from 'pinia';
import { computed, toRef } from 'vue';

import BillingCTA from '@/components/billing/BillingCTA.vue';
import UpsellOverlay from '@/components/billing/UpsellOverlay.vue';
import LearnMoreLink from '@/components/LearnMoreLink.vue';

import useCreditLimits from '@/composables/credits/useCreditLimits';
import type { CreditUsageResponse } from '@/composables/credits/useCreditUsage';
import useBilling from '@/composables/useBilling';
import { FEATURE_FLAG } from '@/constants/feature_flags';
import { useBillingStore, useFeatureFlagStore } from '@/stores';
import { formatNumberWithCommas } from '@/utils';

import UsageLimitCallout, {
  type UsageLimitCalloutContent,
} from './UsageLimitCallout.vue';
import UsageSummaryCard from './UsageSummaryCard.vue';

const props = defineProps<{
  data: CreditUsageResponse | null;
  isLoading: boolean;
  error: unknown;
}>();

const emit = defineEmits(['retry', 'see-usage']);

const {
  creditsUsed,
  creditLimit,
  limitUsedPercent,
  limitUsedWidth,
  creditLimitStatus,
  creditResetDate,
} = useCreditLimits(toRef(props, 'data'));

const billingStore = useBillingStore();
const { isNotPaying, isLegacyConnectorPPv5, hasSubscription } =
  storeToRefs(billingStore);
const { hasFeatureFlag } = useFeatureFlagStore();
const { handleBillingInteraction } = useBilling();

const showPlatformUpsell = computed(
  () => isLegacyConnectorPPv5.value && hasFeatureFlag(FEATURE_FLAG.TEMP_PPV67),
);

const isCreditLimitMasked = computed(
  () => !hasFeatureFlag('temp_mcp_credit_check'),
);

const meterColor = computed(() =>
  isCreditLimitMasked.value ? 'secondary' : creditLimitStatus.value,
);

const creditResetLabel = computed(() =>
  isCreditLimitMasked.value
    ? 'Credits reset annually'
    : 'Credits reset annually on ',
);

const isOrderFormLegacy = computed(
  () => isLegacyConnectorPPv5.value && !hasSubscription.value,
);

const platformUpsellInteraction = computed(() => ({
  cta: PLAN_AND_BILLING_CTAS.CREDIT_USAGE_SUMMARY_CARD,
  event_site: EVENT_SITES.CREDIT_USAGE_SUMMARY_CARD,
  isPlatformUpgrade: true,
  talkToSalesReason: isOrderFormLegacy.value
    ? 'Upgrade to Platform Connector'
    : undefined,
}));

const creditLimitCalloutContent = computed<UsageLimitCalloutContent | null>(
  () => {
    if (isCreditLimitMasked.value) return null;
    if (creditLimitStatus.value === 'warning') {
      return {
        title: "You're almost out of credits",
        subtitle: `Data access pauses at ${formatNumberWithCommas(creditLimit.value ?? 0)} credits, contact our team to discuss options`,
        type: 'warning',
      };
    }
    if (creditLimitStatus.value === 'danger') {
      return {
        title: 'Data access is paused',
        subtitle: 'Contact our team to discuss options and restore access',
        type: 'danger',
      };
    }
    return null;
  },
);

const footerMessage = computed(() =>
  isNotPaying.value
    ? 'Upgrade to Connector for more credits'
    : 'Contact us to add more credits for your annual plan',
);

// Free plan → self-serve Connector checkout (skipModal routes straight there).
function onUpgradeClicked() {
  handleBillingInteraction(
    {
      cta: PLAN_AND_BILLING_CTAS.CREDIT_LIMIT,
      event_site: EVENT_SITES.CREDIT_USAGE_SUMMARY_CARD,
      isPlatformUpgrade: true,
    },
    null,
    true,
  );
}

// Paid plan → Talk to Sales (talkToSalesReason opens the HubSpot form).
function onContactUsClicked() {
  handleBillingInteraction({
    cta: PLAN_AND_BILLING_CTAS.CREDIT_LIMIT,
    talkToSalesReason: 'Add more credits',
    event_site: EVENT_SITES.CREDIT_USAGE_SUMMARY_CARD,
  });
}

function onCreditLimitContactUs() {
  handleBillingInteraction({
    cta: PLAN_AND_BILLING_CTAS.CREDIT_LIMIT,
    talkToSalesReason: 'Credit limit',
    event_site: EVENT_SITES.CREDIT_LIMIT_CALLOUT,
  });
}
</script>

<style lang="pcss" scoped>
@reference "../../css/theme.css";
:deep(
    .c-bitts-empty-state-large
      .c-bitts-empty-state__content
      .c-bitts-empty-state-title
  ),
:deep(.c-bitts-empty-state-large .c-bitts-empty-state__content) {
  @apply text-sm pb-0;
}
:deep(.c-bitts-empty-state__fa-icon) {
  width: 32px !important;
  height: 32px !important;
}
</style>
